import React, { useState } from "react";
import { useLogin } from "../hooks/useLogin";
import { useRegister } from "../hooks/useRegister";
import { ThemeSwitch } from "./Shared/ThemeSwitch";
import { CheckIcon } from "./Shared/Icons";
import { LoginView } from "./LoginView";
import { RegisterView } from "./RegisterView";
import { RecoverView } from "./RecoverView";
import { NewPasswordView } from "./NewPasswordView";
import kiraLogo from "../../../assets/Kira.png";

interface AuthPageProps {
  auth: ReturnType<typeof useLogin>;
  theme: "light" | "dark";
  toggleTheme: () => void;
}

type ActiveView = "LOGIN" | "REGISTER" | "RECOVER";

/* ── Shell: defined OUTSIDE AuthPage so React never remounts it on re-render ── */
interface ShellProps {
  children: React.ReactNode;
  isDark: boolean;
  theme: "light" | "dark";
  toggleTheme: () => void;
}

const Shell: React.FC<ShellProps> = ({ children, isDark, theme, toggleTheme }) => (
  <div
    className={`flex flex-col min-h-screen w-screen relative transition-all duration-300 ${
      isDark ? "bg-[#1A1A1E] text-white" : "bg-white text-[#212121]"
    }`}
  >
    <div className="bg-sliding-pattern" style={{ backgroundImage: `url(${kiraLogo})` }} />
    <ThemeSwitch theme={theme} toggleTheme={toggleTheme} />

    <div className="flex-grow flex flex-col justify-center max-w-[420px] w-full mx-auto px-6 pt-4 pb-6">
      {/* Logo */}
      <div className="flex justify-center mb-4 relative">
        {isDark && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/10 blur-3xl w-32 h-32 pointer-events-none" />
        )}
        <img
          src={kiraLogo}
          className={`h-28 w-28 object-contain relative z-10 ${isDark ? "drop-shadow-xl" : ""}`}
          alt="Kira"
        />
      </div>

      {children}

      <div className="mt-6 text-center text-[10px] text-gray-400 font-medium">
        Kira · Powered by HOLOCRUXE
      </div>
    </div>
  </div>
);

/* ── LoginError: defined OUTSIDE AuthPage for the same reason ── */
const LoginError: React.FC<{ error: string | null; isDark?: boolean }> = ({ error }) =>
  error ? (
    <div className="flex items-center gap-2.5 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl p-4 mb-5 text-xs font-semibold leading-normal animate-[shakeIn_0.35s_ease]">
      <span>⚠️</span>
      <span>{error}</span>
    </div>
  ) : null;

export function AuthPage({ auth, theme, toggleTheme }: AuthPageProps) {
  const [activeView, setActiveView] = useState<ActiveView>("LOGIN");
  const register = useRegister();

  const isDark = theme === "dark";

  /* ── Authenticated success screen ── */
  if (auth.step === "AUTHENTICATED") {
    return (
      <div
        className={`flex flex-col items-center justify-center min-h-screen w-screen transition-all duration-300 p-6 relative ${
          isDark ? "bg-[#1A1A1E] text-white" : "bg-white text-[#212121]"
        }`}
      >
        <div className="bg-sliding-pattern" style={{ backgroundImage: `url(${kiraLogo})` }} />
        <div className="flex flex-col items-center max-w-sm w-full text-center">
          <div className="w-16 h-16 rounded-full bg-[#058b95] flex items-center justify-center mb-6 shadow-lg shadow-cyan-500/20">
            <CheckIcon />
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight mb-2">¡Bienvenido!</h2>
          <p className={`text-sm ${isDark ? "text-white/40" : "text-gray-500"}`}>
            Sesión iniciada correctamente
          </p>
        </div>
      </div>
    );
  }

  /* ── New Password Required ── */
  if (auth.step === "NEW_PASSWORD") {
    return (
      <Shell isDark={isDark} theme={theme} toggleTheme={toggleTheme}>
        <NewPasswordView auth={auth} isDark={isDark} />
      </Shell>
    );
  }

  /* ── Recover Password ── */
  if (activeView === "RECOVER") {
    return (
      <Shell isDark={isDark} theme={theme} toggleTheme={toggleTheme}>
        <h1 className="text-2xl font-black text-center tracking-tight mb-2">
          Restablecer contraseña
        </h1>
        <RecoverView
          isDark={isDark}
          onCancel={() => setActiveView("LOGIN")}
        />
      </Shell>
    );
  }

  /* ── Register ── */
  if (activeView === "REGISTER") {
    return (
      <Shell isDark={isDark} theme={theme} toggleTheme={toggleTheme}>
        <h1 className="text-2xl font-black text-center tracking-tight mb-2">Creá tu cuenta</h1>
        <RegisterView
          register={register}
          isDark={isDark}
          onCancel={() => {
            register.resetForm();
            setActiveView("LOGIN");
          }}
        />
      </Shell>
    );
  }

  /* ── Login (default) ── */
  return (
    <Shell isDark={isDark} theme={theme} toggleTheme={toggleTheme}>
      <h1 className="text-2xl font-black text-center tracking-tight mb-1">Bienvenido</h1>
      <p className={`text-xs text-center mb-2 ${isDark ? "text-white/40" : "text-gray-500"}`}>
        Ingresá a tu cuenta para continuar
      </p>
      <LoginError error={auth.error} />
      <LoginView
        auth={auth}
        isDark={isDark}
        onShowRegister={() => setActiveView("REGISTER")}
        onShowRecover={() => setActiveView("RECOVER")}
      />
    </Shell>
  );
}
